import { motion } from "framer-motion";
import { ShieldAlert, AlertTriangle, ShieldCheck, PieChart, Activity, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartLegend,
    ChartLegendContent
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Pie, PieChart as RechartsPieChart } from "recharts";

const riskDistributionData = [
    { name: "Operational", value: 5, color: "#3b82f6" },
    { name: "Compliance", value: 3, color: "#10b981" },
    { name: "Strategic", value: 2, color: "#f59e0b" },
    { name: "Technology", value: 4, color: "#8b5cf6" },
];

const chartConfig = {
    value: {
        label: "Risks",
    }
};

export default function RiskOverview() {
    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-medium text-foreground">Risk Overview</h1>
                <p className="text-muted-foreground">
                    Monitor your organization's risk posture and mitigation efforts at a glance.
                </p>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[
                    { title: "Total Risks", value: "14", icon: Activity, description: "Active identified risks", color: "blue" },
                    { title: "High Risks", value: "4", icon: ShieldAlert, description: "Critical impact levels", color: "red" },
                    { title: "Gaps Found", value: "3", icon: AlertTriangle, description: "Risks with no linked policy", color: "amber" },
                    { title: "Risk Appetite", value: "Within", icon: ShieldCheck, description: "Organization posture", color: "green" },
                ].map((stat, i) => (
                    <motion.div
                        key={stat.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                    >
                        <Card className="relative overflow-hidden border-none shadow-premium transition-all hover:translate-y-[-2px] hover:shadow-premium-hover group">
                            <div className={`absolute top-0 right-0 p-3 opacity-10 group-hover:scale-110 transition-transform`}>
                                <stat.icon size={48} className={`text-${stat.color}-500`} />
                            </div>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                    {stat.title}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{stat.value}</div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {stat.description}
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}
            </div>

            {/* Charts Section */}
            <div className="grid gap-6 md:grid-cols-2">
                <Card className="border-none shadow-premium overflow-hidden border-t-2 border-primary/20">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <PieChart className="h-5 w-5 text-primary" />
                            Risk Distribution by Category
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="h-[300px]">
                        <ChartContainer config={chartConfig}>
                            <BarChart data={riskDistributionData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12 }}
                                />
                                <YAxis hide />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <Bar
                                    dataKey="value"
                                    radius={[4, 4, 0, 0]}
                                >
                                    {riskDistributionData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ChartContainer>
                    </CardContent>
                </Card>

                <Card className="border-none shadow-premium overflow-hidden border-t-2 border-primary/20">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            Risk Trend Summary
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col justify-center items-center h-[300px] text-center p-8">
                        <div className="p-4 rounded-full bg-primary/10 mb-4 animate-pulse">
                            <ShieldCheck className="h-12 w-12 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2 italic">Posture Assessment</h3>
                        <p className="text-muted-foreground">
                            Your overall risk posture is <span className="text-emerald-500 font-bold uppercase tracking-wider">Stable</span>.
                            The most significant recent movement was in <span className="font-medium text-primary">Technology Risk</span> which increased by 12%.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
